import React, { Component, ReactNode } from 'react';
import CustomAnimateButton from '../../custom/CustomAnimateButton';
import Icon from '../../../icons/Icon';
import { Group, Role, User } from '../../../domain/rbac/RbacDomains';
import GroupService, { SaveGroupDto } from 'service/GroupService';
import { CommonRoles, MemberPermission } from '../../../constant/Constants';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomMultiSelectBox from '../../custom/CustomMultiSelectBox';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaInput from 'components/custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import MessageUtil from '../../../util/MessageUtil';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class GroupEditComponent extends Component<Props, State> {
  public state = new State();

  private readonly groupService: GroupService = ServiceContainer.getInstance().get(ServiceNames.GROUP);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render(): ReactNode {
    const { selectedUsers, selectedRoles, groupName, groupDescription, saveLoading } = this.state;
    const { isCreate, users, roles, preview, checkerRequests } = this.props;
    const userMultiSelectItems = users.map((user) => ({ label: user.username, value: user.username }));
    const roleMultiSelectItems = roles.map((role) => ({ label: role.name, value: role.name }));

    return (
      <div className={'flex flex-col items-center w-full  lg:h-full'}>
        <div className={'flex flex-col items-center w-full h-full  gap-x-2 gap-y-4 border-f-gray-2 pb-5'}>
          <div className={'flex flex-col gap-y-1.5 w-full'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.groupName', defaultMessage: 'Group Name' })} />
            <FormicaInput
              value={groupName}
              onChange={(e) => {
                this.setState({ groupName: e.currentTarget.value });
              }}
              disabled={!isCreate || !RoleHelper.canManage('Group') || preview}
              name={'name'}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            />
          </div>
          <div className={'flex flex-col gap-y-1.5 w-full'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.groupDescription', defaultMessage: 'Group Description' })} />
            <FormicaInput
              value={groupDescription}
              name={'description'}
              disabled={!RoleHelper.canManage('Group') || preview}
              onChange={(e) => {
                this.setState({ groupDescription: e.currentTarget.value });
              }}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            />
          </div>
          <CustomMultiSelectBox
            options={roleMultiSelectItems}
            selectedItems={selectedRoles}
            label={LanguageUtil.get({ id: 'common.label.roles', defaultMessage: 'Roles' })}
            onChangeSelectedItems={(e: any[]) => this.setState({ selectedRoles: e })}
          />
          <CustomMultiSelectBox
            options={userMultiSelectItems}
            selectedItems={selectedUsers}
            onChangeSelectedItems={(e: any[]) => {
              this.setState({ selectedUsers: e });
            }}
            label={LanguageUtil.get({ id: 'common.label.users', defaultMessage: 'Users' })}
          />
        </div>
        <div className={'flex flex-col gap-x-2 gap-y-5 w-full items-center'}>
          <CustomAnimateButton
            onClick={this.createOrUpdateGroup}
            loading={saveLoading}
            disabled={
              !RoleHelper.canManage('Group') ||
              this.environmentUtil.isLdapOnly() ||
              !!checkerRequests.find((c) => c.component == ComponentName.ADMIN_GROUP && c.documentId == groupName) ||
              preview
            }
            className={'self-end absolute bottom-2'}
            text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
            customSize={'sm'}
            customType={'save'}
            icon={<Icon icon={'save-with-bg'} size={34} />}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const { isCreate, selectedGroup } = this.props;
    if (!isCreate && selectedGroup) {
      this.setState({
        groupDescription: selectedGroup.description,
        groupName: selectedGroup.name,
        selectedRoles: selectedGroup.roles,
        selectedUsers: this.resolveSelectedUsers(selectedGroup),
        enabled: selectedGroup.enabled ?? true,
        createdAt: selectedGroup.createdAt,
        updatedAt: selectedGroup.updatedAt,
      });
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { selectedGroup } = this.props;
    if (prevProps.selectedGroup != selectedGroup) {
      if (selectedGroup) {
        this.setState({
          groupDescription: selectedGroup.description,
          groupName: selectedGroup.name,
          selectedRoles: selectedGroup.roles,
          selectedUsers: this.resolveSelectedUsers(selectedGroup),
          enabled: selectedGroup.enabled ?? true,
          createdAt: selectedGroup.createdAt,
          updatedAt: selectedGroup.updatedAt,
        });
      }
    }
  }

  private resolveSelectedUsers = (group: Group): string[] => {
    const { users } = this.props;
    const membersFromUsers = users.filter((user) => (user.groups || []).includes(group.name)).map((user) => user.username);
    const membersFromGroup = group.users || [];
    return [...new Set([...membersFromUsers, ...membersFromGroup])];
  };

  private createOrUpdateGroup = () => {
    const { isCreate, preview, checkerRequests } = this.props;
    const { groupName } = this.state;
    if (preview) return;
    if (this.environmentUtil.isLdapOnly()) return;
    if (!!checkerRequests.find((c) => c.component == ComponentName.ADMIN_GROUP && c.documentId == groupName)) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' }),
        detail: LanguageUtil.get({
          id: 'messages.warning.waitingApproval',
          defaultMessage: "Create or Update request has been already sent to other admin users, you can't send new request",
        }),
        life: 4000,
      });
      return;
    }
    if (this.environmentUtil.isLdapOnly()) return;
    this.setState({ saveLoading: true });
    if (isCreate) {
      this.createGroup();
    } else {
      this.updateGroup();
    }
  };

  private updateGroup = async () => {
    const { groupName, permission, selectedRoles, selectedUsers, selectedRole, groupDescription, enabled, createdAt, updatedAt } = this.state;
    const { selectedGroup, onHide } = this.props;
    if (selectedGroup == undefined) {
      return;
    }
    const succeed = await this.groupService.updateGroup({
      groupName,
      groupDescription,
      permission,
      selectedUsers,
      selectedRoles,
      selectedRole,
      enabled,
      createdAt,
      updatedAt,
    });
    if (succeed) {
      onHide?.();
    }
    this.setState({ saveLoading: false });
  };

  private createGroup = async () => {
    const { groupName, selectedRole, permission, selectedUsers, selectedRoles, groupDescription, createdAt, updatedAt } = this.state;
    const { onHide } = this.props;
    const dto: SaveGroupDto = { groupName, selectedRole, selectedRoles, selectedUsers, groupDescription, permission, enabled: true, createdAt, updatedAt };
    try {
      const succeed = await this.groupService.saveGroup(dto);
      if (succeed) {
        onHide?.();
      }
      this.setState({ saveLoading: false });
    } catch (e) {}
  };
}

interface Props {
  isCreate?: boolean;
  selectedGroup?: Group | undefined;
  onChangeGroup: (group: Group) => void;
  roles: Role[];
  users: User[];
  onHide: () => void;
  checkerRequests: CheckerRequest[];
  preview?: boolean;
}

class State {
  groupName: string = '';
  groupDescription: string = '';
  selectedRole: CommonRoles = 'Admin';
  selectedRoles: string[] = [];
  selectedUsers: string[] = [];
  permission: MemberPermission = 0;
  enabled: boolean = true;
  saveLoading: boolean = false;
  createdAt: number | undefined;
  updatedAt: number | undefined;
}

export default GroupEditComponent;
